import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, 
  Shield, 
  PiggyBank, 
  AlertTriangle, 
  Info, 
  TrendingUp,
  Clock,
  Heart,
  Building2,
  ExternalLink,
  CheckCircle,
  XCircle,
  Calendar,
  FileText
} from 'lucide-react';
import api from '../lib/api';

const InfosPratiquesPage = () => {
  const [infos, setInfos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('taux');

  const fetchInfos = useCallback(async () => {
    try {
      const response = await api.get('/infos-pratiques');
      setInfos(response.data);
    } catch (error) {
      // Error loading infos - fail silently in production
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfos();
  }, [fetchInfos]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="infos-pratiques-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Infos Pratiques</h1>
        <p className="text-muted-foreground">Tout ce que vous devez savoir sur le statut auto-entrepreneur</p>
        <Badge variant="outline" className="mt-2">
          Mis à jour : {infos?.derniere_maj || 'Janvier 2026'}
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="taux" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Taux 2026
          </TabsTrigger>
          <TabsTrigger value="calendrier" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Calendrier
          </TabsTrigger>
          <TabsTrigger value="protection" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Protection Sociale
          </TabsTrigger>
          <TabsTrigger value="retraite" className="flex items-center gap-2">
            <PiggyBank className="h-4 w-4" />
            Retraite
          </TabsTrigger>
        </TabsList>

        {/* ONGLET TAUX */}
        <TabsContent value="taux" className="space-y-6 mt-6">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">Évolution des taux BNC en cours</p>
                  <p className="text-sm text-amber-800">
                    Les taux de cotisations pour les BNC augmentent progressivement jusqu'en 2026 
                    pour améliorer votre retraite complémentaire.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {/* BIC Vente */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>BIC Vente</span>
                  <Badge className="text-lg px-3 py-1 bg-emerald-100 text-emerald-800">
                    {infos?.taux_2025?.bic_vente?.taux}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Achat/revente de marchandises, e-commerce, vente de produits
                </p>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Plafond CA annuel</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(infos?.taux_2025?.bic_vente?.plafond_ca)}
                  </p>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Exemples d'activités :</p>
                  <div className="flex flex-wrap gap-1">
                    {infos?.taux_2025?.bic_vente?.exemples?.map((ex, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{ex}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BIC Service */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>BIC Service</span>
                  <Badge className="text-lg px-3 py-1 bg-blue-100 text-blue-800">
                    {infos?.taux_2025?.bic_service?.taux}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Artisans et prestataires de services commerciaux
                </p>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Plafond CA annuel</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(infos?.taux_2025?.bic_service?.plafond_ca)}
                  </p>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Exemples d'activités :</p>
                  <div className="flex flex-wrap gap-1">
                    {infos?.taux_2025?.bic_service?.exemples?.map((ex, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{ex}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BNC Régime Général */}
            <Card className="border-amber-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>BNC Régime Général</span>
                  <Badge className="text-lg px-3 py-1 bg-amber-100 text-amber-800">
                    {infos?.taux_2025?.bnc?.taux}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Professions libérales non réglementées
                </p>
                <div className="p-3 bg-muted rounded-lg mb-3">
                  <p className="text-sm font-medium">Plafond CA annuel</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(infos?.taux_2025?.bnc?.plafond_ca)}
                  </p>
                </div>
                
                {/* Évolution des taux */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Évolution progressive des taux
                  </p>
                  <div className="space-y-1">
                    {infos?.taux_2025?.bnc?.evolution?.historique?.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className={item.periode === "Depuis 2026" ? "font-bold" : "text-muted-foreground"}>
                          {item.periode}
                        </span>
                        <span className={item.periode === "Depuis 2026" ? "font-bold text-amber-700" : "text-muted-foreground"}>
                          {item.taux}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Exemples d'activités :</p>
                  <div className="flex flex-wrap gap-1">
                    {infos?.taux_2025?.bnc?.exemples?.map((ex, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{ex}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BNC CIPAV */}
            <Card className="border-purple-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>BNC CIPAV</span>
                  <Badge className="text-lg px-3 py-1 bg-purple-100 text-purple-800">
                    {infos?.taux_2025?.bnc_cipav?.taux}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Professions libérales réglementées affiliées à la CIPAV
                </p>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Plafond CA annuel</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(infos?.taux_2025?.bnc_cipav?.plafond_ca)}
                  </p>
                </div>
                <div className="mt-3 p-2 bg-purple-50 rounded text-xs text-purple-800">
                  <Info className="h-3 w-3 inline mr-1" />
                  {infos?.taux_2025?.bnc_cipav?.info}
                </div>
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Exemples d'activités :</p>
                  <div className="flex flex-wrap gap-1">
                    {infos?.taux_2025?.bnc_cipav?.exemples?.map((ex, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{ex}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ONGLET CALENDRIER FISCAL */}
        <TabsContent value="calendrier" className="space-y-6 mt-6">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900">Calendrier fiscal Auto-Entrepreneur 2026</p>
                  <p className="text-sm text-blue-800">
                    Retrouvez toutes les dates clés pour vos déclarations URSSAF, TVA, CFE et impôts.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* URSSAF */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Déclarations URSSAF
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold text-sm mb-2">📅 Déclaration Mensuelle</p>
                  <p className="text-sm text-muted-foreground mb-2">Avant le dernier jour du mois suivant</p>
                  <div className="space-y-1 text-xs">
                    <p>• CA janvier → avant le 28 février</p>
                    <p>• CA février → avant le 31 mars</p>
                    <p>• etc.</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold text-sm mb-2">📅 Déclaration Trimestrielle</p>
                  <p className="text-sm text-muted-foreground mb-2">Avant le dernier jour du mois suivant le trimestre</p>
                  <div className="space-y-1 text-xs">
                    <p>• T1 (jan-mar) → avant le 30 avril</p>
                    <p>• T2 (avr-juin) → avant le 31 juillet</p>
                    <p>• T3 (juil-sept) → avant le 31 octobre</p>
                    <p>• T4 (oct-déc) → avant le 31 janvier</p>
                  </div>
                </div>
              </div>
              <a 
                href="https://www.autoentrepreneur.urssaf.fr/portail/accueil/une-question/toutes-les-fiches-pratiques/declarer-et-payer-mes-cotisation.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Guide officiel URSSAF
              </a>
            </CardContent>
          </Card>

          {/* TVA */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Régimes TVA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold text-sm text-green-900 mb-2">Franchise de TVA</p>
                  <Badge variant="outline" className="mb-2 text-green-700">Pas de déclaration</Badge>
                  <p className="text-xs text-green-800">
                    Si CA &lt; seuils (37 500€ services / 85 000€ vente)
                  </p>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-semibold text-sm text-amber-900 mb-2">Réel Simplifié</p>
                  <Badge variant="outline" className="mb-2 text-amber-700">Annuel + acomptes</Badge>
                  <div className="text-xs text-amber-800 space-y-1">
                    <p>• <strong>CA12</strong> : ~5 mai</p>
                    <p>• <strong>Acompte 1</strong> : 15 juillet (55%)</p>
                    <p>• <strong>Acompte 2</strong> : 15 décembre (40%)</p>
                  </div>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-semibold text-sm text-red-900 mb-2">Réel Normal</p>
                  <Badge variant="outline" className="mb-2 text-red-700">Mensuel ou trimestriel</Badge>
                  <div className="text-xs text-red-800 space-y-1">
                    <p>• <strong>Mensuel</strong> : 15-24 du mois suivant</p>
                    <p>• <strong>Trimestriel</strong> : fin du mois suivant</p>
                    <p className="text-[10px] italic">Dates exactes selon nom/département</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CFE */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                CFE (Cotisation Foncière des Entreprises)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-bold text-lg">15 décembre</p>
                    <p className="text-sm text-muted-foreground">Date limite de paiement</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <p>• <strong>Avis disponible</strong> : mi-novembre sur impots.gouv.fr</p>
                  <p>• <strong>Exonération 1ère année</strong> : pas de CFE la 1ère année d'activité</p>
                  <p>• <strong>Exonération CA</strong> : si CA &lt; 5 000€ annuel</p>
                </div>
              </div>
              <a 
                href="https://www.impots.gouv.fr/professionnel/questions/comment-puis-je-acceder-mon-avis-de-cfe-et-le-payer" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Payer ma CFE sur impots.gouv.fr
              </a>
            </CardContent>
          </Card>

          {/* Impôt sur le revenu */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-primary" />
                Impôt sur le Revenu
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold text-sm mb-2">Versement Libératoire</p>
                  <p className="text-sm text-muted-foreground mb-2">Prélevé avec les cotisations URSSAF</p>
                  <div className="space-y-1 text-xs">
                    <p>• 1% (vente)</p>
                    <p>• 1,7% (BIC service)</p>
                    <p>• 2,2% (BNC)</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold text-sm mb-2">Barème classique</p>
                  <p className="text-sm text-muted-foreground mb-2">Déclaration annuelle</p>
                  <div className="space-y-1 text-xs">
                    <p>• <strong>Avril-Mai</strong> : déclaration en ligne</p>
                    <p>• <strong>Septembre</strong> : réception avis d'imposition</p>
                    <p>• Prélèvement à la source ou acomptes</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Récapitulatif annuel */}
          <Card className="border-primary">
            <CardHeader className="pb-3 bg-primary/5">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Récapitulatif Annuel 2026
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                <div className="p-3 flex justify-between items-center">
                  <span className="font-medium">Janvier</span>
                  <span className="text-sm text-muted-foreground">Déclaration T4 URSSAF (si trimestriel)</span>
                </div>
                <div className="p-3 flex justify-between items-center">
                  <span className="font-medium">Avril-Mai</span>
                  <span className="text-sm text-muted-foreground">Déclaration revenus + CA12 TVA</span>
                </div>
                <div className="p-3 flex justify-between items-center">
                  <span className="font-medium">15 Juillet</span>
                  <span className="text-sm text-muted-foreground">Acompte TVA 55% (réel simplifié)</span>
                </div>
                <div className="p-3 flex justify-between items-center">
                  <span className="font-medium">15 Décembre</span>
                  <span className="text-sm text-muted-foreground">CFE + Acompte TVA 40%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ONGLET PROTECTION SOCIALE */}
        <TabsContent value="protection" className="space-y-6 mt-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">Point d'attention</p>
                  <p className="text-sm text-amber-800">
                    <strong>Tous les auto-entrepreneurs</strong> (BIC et BNC) ont un délai de carence de <strong>3 jours</strong>. 
                    Pensez à souscrire une prévoyance complémentaire pour mieux vous protéger !
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparatif délais de carence */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-emerald-500" />
                  Professions Libérales (BNC)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-6 bg-emerald-50 rounded-xl mb-4">
                  <p className="text-5xl font-bold text-emerald-600">3</p>
                  <p className="text-lg font-medium text-emerald-800">jours de carence</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Même délai de carence que les artisans/commerçants (3 jours).
                </p>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    IJ dès le 4ème jour d'arrêt
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-emerald-500" />
                  Artisans / Commerçants (BIC)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-6 bg-emerald-50 rounded-xl mb-4">
                  <p className="text-5xl font-bold text-emerald-600">3</p>
                  <p className="text-lg font-medium text-emerald-800">jours de carence</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Les artisans et commerçants (BIC) ont un délai de carence de 3 jours après 1 an d'affiliation.
                </p>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    IJ dès le 4ème jour d'arrêt
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Couvertures */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary" />
                Ce que couvrent vos cotisations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">70%</p>
                  <p className="text-sm text-muted-foreground">Soins médicaux</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">80%</p>
                  <p className="text-sm text-muted-foreground">Hospitalisation</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">100%</p>
                  <p className="text-sm text-muted-foreground">Maternité</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                  <p className="text-2xl font-bold text-red-600">0%</p>
                  <p className="text-sm text-red-800">Chômage</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                {infos?.protection_sociale?.conditions_ij}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ONGLET RETRAITE */}
        <TabsContent value="retraite" className="space-y-6 mt-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">Alerte importante</p>
                  <p className="text-sm text-red-800">
                    {infos?.retraite?.alerte}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparatif AE vs EI */}
          <Card>
            <CardHeader>
              <CardTitle>Comparatif Retraite : Auto-Entrepreneur vs Entreprise Individuelle</CardTitle>
              <p className="text-sm text-muted-foreground">
                Pour 25 ans de cotisations avec un CA mensuel de 2 000€
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="h-5 w-5 text-red-600" />
                    <h3 className="font-bold text-red-900">Auto-Entrepreneur</h3>
                  </div>
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-red-600">
                      {infos?.retraite?.comparatif_25_ans?.ae?.pension_min}€ - {infos?.retraite?.comparatif_25_ans?.ae?.pension_max}€
                    </p>
                    <p className="text-muted-foreground">par mois estimé</p>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      Cotisations retraite faibles (environ 0.5% à 2.2% du CA selon l'activité)
                    </p>
                  </div>
                </div>

                <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-bold text-emerald-900">Entreprise Individuelle (EI)</h3>
                  </div>
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-emerald-600">
                      {infos?.retraite?.comparatif_25_ans?.ei_classique?.pension_min}€ - {infos?.retraite?.comparatif_25_ans?.ei_classique?.pension_max}€
                    </p>
                    <p className="text-muted-foreground">par mois estimé</p>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <strong>Option IR ou IS possible</strong><br/>
                      Cotisations sociales : environ 45% du revenu (comme SARL)
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-900 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Différence : {infos?.retraite?.comparatif_25_ans?.difference}
                </p>
                <p className="text-sm text-amber-800 mt-2">
                  {infos?.retraite?.conseil}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sources */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-2">Sources officielles :</p>
          <div className="flex flex-wrap gap-4">
            {infos?.sources?.map((source, i) => (
              <a 
                key={i}
                href={source.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                {source.nom}
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InfosPratiquesPage;
